import { useCallback, useReducer } from "react";
import produce from "immer";
import { v4 as uuidv4 } from "uuid";
import {
	ElementType,
	ElementTypeList,
	ElementSchema,
	businessLogicValue,
} from "schemas/elements";
import elementSchema from "schemas";
import { SearchSelectOptionProps } from "@alm/flow-ds/components";

export type IdData = SearchSelectOptionProps[] | undefined;

export type FormEntity = { value: string | null; label: string; data?: IdData };

export type FormSetup = {
	formId: string;
	formEntity: FormEntity;
	usePatch: boolean;
};

export type State = FormSetup & {
	validation: boolean;
	formLabel: string;
	elements: ElementType[];
	loading: boolean;
	onLoad?: businessLogicValue;
	beforeSave?: businessLogicValue;
	beforeSaveValidation: boolean;
	onLoadValidation: boolean;
	formLayout: FormLayout;
	selectFieldsBasedOnForm: boolean;
	additionalSelect?: SearchSelectOptionProps[];
};

export type MoveData = {
	initial: number;
	destination: number;
};

export type UpdateData = {
	elementIndex: number;
	property: propertyType;
	// any because it can be any value depending on what property is updated
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	value: any;
	nestedField?: "search";
};

export type ElementValidation = {
	elementIndex: number;
	validation: boolean;
};

export type NestedType = {
	nestedElements: ElementType[];
	nestedValidation: boolean;
	nestedFormEntity: FormEntity;
};

export type BusinessLogicUpdateData<T> = {
	elementIndex?: number;
	property: T;
	value: businessLogicValue;
};

export type FormBusinessLogicUpdateDataValidation = {
	property: formBusinessLogicPropertyValidation;
	validation: boolean;
};

export type FormLayout = {
	fullWidth: boolean;
	maxWidth: number | null;
};

export type SelectFieldsType = {
	value: boolean;
	isEmu: boolean;
};

export type propertyType =
	| "id"
	| "name"
	| "label"
	| "required"
	| "disabled"
	| "hint"
	| "validation"
	| "options"
	| "optionsId"
	| "optionsPath"
	| "optionsFilter"
	| "searchField"
	| "search"
	| "searchOperator"
	| "payload"
	| "populatesFrom"
	| "populatesKey"
	| "elements"
	| "value"
	| "looksAt"
	| "resource"
	| "allowCreation"
	| "labelField"
	| "link"
	| "afterPatch"
	| "isArray"
	| "limit"
	| "numberType"
	| "numberFormat"
	| "loadCollapsed"
	| "allowEmptyStringAsNull"
	| "elementLayout"
	| "createLink"
	| "updateLink";

export type formBusinessLogicPropertyType = "onLoad" | "beforeSave";

export type formBusinessLogicPropertyValidation =
	| "onLoadValidation"
	| "beforeSaveValidation";

export type businessLogicPropertyType =
	| formBusinessLogicPropertyType
	| "afterPatch";

// Actions
const ADD_ELEMENT = "ADD_ELEMENT";
const MOVE_ELEMENT = "MOVE_ELEMENT";
const REMOVE_ELEMENT = "REMOVE_ELEMENT";
const UPDATE_ELEMENT = "UPDATE_ELEMENT";
const INIT_FORM = "INIT_FORM";
const UPDATE_FORM_LABEL = "UPDATE_FORM_LABEL";
const UPDATE_ENTITY_DATA = "UPDATE_ENTITY_DATA";
const UPDATE_VALIDATION = "UPDATE_VALIDATION";
// const UPDATE_USE_PATCH = "UPDATE_USE_PATCH";
const UPDATE_ELEMENT_VALIDATION = "UPDATE_ELEMENT_VALIDATION";
const INIT_NESTED_ELEMENTS = "INIT_NESTED_ELEMENTS";
const LOAD_FORM = "LOAD_FORM";
const UPDATE_FORM_BUSINESS_LOGIC = "UPDATE_FORM_BUSINESS_LOGIC";
const UPDATE_FORM_BUSINESS_LOGIC_VALIDATION =
	"UPDATE_FORM_BUSINESS_LOGIC_VALIDATION";
const UPDATE_FORM_LAYOUT_FULL_WIDTH = "UPDATE_FORM_LAYOUT_FULL_WIDTH";
const UPDATE_FORM_LAYOUT_MAX_WIDTH = "UPDATE_FORM_LAYOUT_MAX_WIDTH";
const UPDATE_SELECT_FIELDS_BASED_ON_FORM = "UPDATE_SELECT_FIELDS_BASED_ON_FORM";
const UPDATE_ADDITIONAL_SELECT = "UPDATE_ADDITIONAL_SELECT";

interface AddElementAction {
	type: typeof ADD_ELEMENT;
	data: ElementType;
}

interface MoveElementAction {
	type: typeof MOVE_ELEMENT;
	data: MoveData;
}

interface RemoveElementAction {
	type: typeof REMOVE_ELEMENT;
	data: number;
}

interface UpdateElementAction {
	type: typeof UPDATE_ELEMENT;
	data: UpdateData;
}

interface InitFormAction {
	type: typeof INIT_FORM;
	data: FormSetup;
}

interface UpdateFormLabelAction {
	type: typeof UPDATE_FORM_LABEL;
	data: string;
}

interface UpdateEntityDataAction {
	type: typeof UPDATE_ENTITY_DATA;
	data: IdData;
}

interface UpdateValidationAction {
	type: typeof UPDATE_VALIDATION;
	data: boolean;
}

interface UpdateElementValidationAction {
	type: typeof UPDATE_ELEMENT_VALIDATION;
	data: ElementValidation;
}

interface InitNestedElements {
	type: typeof INIT_NESTED_ELEMENTS;
	data: NestedType;
}

interface LoadForm {
	type: typeof LOAD_FORM;
	data: State;
}
interface UpdateFormBusinessLogic {
	type: typeof UPDATE_FORM_BUSINESS_LOGIC;
	data: BusinessLogicUpdateData<formBusinessLogicPropertyType>;
}

interface UpdateFormBusinessLogicValidation {
	type: typeof UPDATE_FORM_BUSINESS_LOGIC_VALIDATION;
	data: FormBusinessLogicUpdateDataValidation;
}

interface UpdateFormLayoutFullWidthAction {
	type: typeof UPDATE_FORM_LAYOUT_FULL_WIDTH;
	data: boolean;
}

interface UpdateFormLayoutMaxWidthAction {
	type: typeof UPDATE_FORM_LAYOUT_MAX_WIDTH;
	data: number | null;
}

interface UpdateSelectFieldsBasedOnFormAction {
	type: typeof UPDATE_SELECT_FIELDS_BASED_ON_FORM;
	data: SelectFieldsType;
}

interface UpdateAdditionalSelectAction {
	type: typeof UPDATE_ADDITIONAL_SELECT;
	data: SearchSelectOptionProps[];
}

type Actions =
	| AddElementAction
	| MoveElementAction
	| RemoveElementAction
	| UpdateElementAction
	| InitFormAction
	| UpdateFormLabelAction
	| UpdateEntityDataAction
	| UpdateValidationAction
	| UpdateElementValidationAction
	| InitNestedElements
	| LoadForm
	| UpdateFormBusinessLogic
	| UpdateFormBusinessLogicValidation
	| UpdateFormLayoutFullWidthAction
	| UpdateFormLayoutMaxWidthAction
	| UpdateSelectFieldsBasedOnFormAction
	| UpdateAdditionalSelectAction;

export const initialState: State = {
	formId: "",
	formEntity: {
		value: "",
		label: "",
		data: [
			{
				label: "",
				value: "",
			},
		],
	},
	formLayout: {
		fullWidth: false,
		maxWidth: null,
	},
	validation: false,
	formLabel: "",
	elements: [],
	usePatch: false,
	loading: false,
	onLoadValidation: false,
	beforeSaveValidation: false,
	selectFieldsBasedOnForm: false,
};

export const runElementValidation = (
	elements: ElementType[],
	data: boolean
) => {
	elements.forEach(element => {
		element.elementValidation = data;
		if (element.elements && element.elements.length > 0) {
			element.elements = runElementValidation(element.elements, data);
		}
		if (element.search?.view && element.search.view.length > 0) {
			element.search.view = runElementValidation(
				element.search.view,
				data
			);
		}
	});
	return elements;
};

export const reducer = (state: State, action: Actions): State => {
	return produce(state, draft => {
		switch (action.type) {
			case ADD_ELEMENT:
				const element = action.data;
				draft.elements = [...draft.elements, element];
				break;

			case MOVE_ELEMENT:
				const { initial, destination } = action.data;
				const elementToMove = draft.elements.splice(initial, 1)[0];
				draft.elements.splice(destination, 0, elementToMove);
				break;

			case REMOVE_ELEMENT:
				const index = action.data;
				draft.elements.splice(index, 1);
				break;

			case UPDATE_ELEMENT:
				{
					const { elementIndex, property, value, nestedField } =
						action.data;
					if (nestedField !== undefined) {
						const search =
							draft.elements[elementIndex][nestedField];
						if (search !== undefined) {
							draft.elements[elementIndex][nestedField] = {
								...search,
								[property]: value,
							};
						} else {
							draft.elements[elementIndex][nestedField] = {
								columns: [],
								payload: {
									filter: [],
									sort: [],
									"page-size": 10,
								},
								[property]: value,
							};
						}
					} else {
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						(draft.elements as any)[elementIndex][property] = value;
					}
				}
				break;

			case INIT_FORM:
				const { formId, formEntity, usePatch } = action.data;
				draft.formId = formId;
				draft.formEntity = formEntity;
				draft.usePatch = usePatch;
				break;

			case UPDATE_FORM_LABEL:
				draft.formLabel = action.data;
				break;

			case UPDATE_ENTITY_DATA:
				draft.formEntity.data = action.data;
				break;

			case UPDATE_VALIDATION:
				draft.validation = action.data;
				draft.elements.forEach(element => {
					element.elementValidation = action.data;
				});
				draft.elements = runElementValidation(
					draft.elements,
					action.data
				);
				break;

			case UPDATE_ELEMENT_VALIDATION:
				const { elementIndex: validationIndex, validation } =
					action.data;
				draft.elements[validationIndex].formValidation = validation;
				break;

			case INIT_NESTED_ELEMENTS:
				const { nestedElements, nestedValidation, nestedFormEntity } =
					action.data;
				nestedElements
					? (draft.elements = nestedElements)
					: (draft.elements = []);
				if (nestedValidation) draft.validation = nestedValidation;
				draft.formEntity = nestedFormEntity;
				break;

			case LOAD_FORM:
				draft.formEntity = action.data.formEntity;
				draft.elements = action.data.elements;
				draft.formId = action.data.formId;
				draft.formLabel = action.data.formLabel;
				draft.loading = action.data.loading;
				draft.validation = action.data.validation;
				draft.beforeSave = action.data.beforeSave;
				draft.onLoad = action.data.onLoad;
				draft.usePatch = action.data.usePatch;
				if (action.data.formLayout !== undefined)
					draft.formLayout = action.data.formLayout;
				draft.selectFieldsBasedOnForm =
					action.data.selectFieldsBasedOnForm;
				if (action.data.additionalSelect !== undefined)
					draft.additionalSelect = action.data.additionalSelect;
				break;

			case UPDATE_FORM_BUSINESS_LOGIC:
				{
					const { property, value } = action.data;
					draft[property] = value;
				}
				break;
			case UPDATE_FORM_BUSINESS_LOGIC_VALIDATION:
				{
					const { property, validation } = action.data;
					draft[property] = validation;
				}
				break;
			case UPDATE_FORM_LAYOUT_FULL_WIDTH:
				draft.formLayout.fullWidth = action.data;
				break;
			case UPDATE_FORM_LAYOUT_MAX_WIDTH:
				draft.formLayout.maxWidth = action.data;
				break;

			case UPDATE_SELECT_FIELDS_BASED_ON_FORM:
				draft.selectFieldsBasedOnForm = action.data.value;
				if (action.data.isEmu === false)
					draft.usePatch = action.data.value;
				break;
			case UPDATE_ADDITIONAL_SELECT:
				draft.additionalSelect = action.data;
				break;
		}
	});
};

export default (init: State = initialState) => {
	const [state, dispatch] = useReducer(reducer, init);

	const addElement = useCallback((data: ElementTypeList) => {
		const jsonRequired: propertyType[] = [];
		const thisElement = (elementSchema as ElementSchema)[data];
		const booleanData: {
			allowCreation?: boolean;
			isArray?: boolean;
			searchOperator?: string;
			numberType?: string;
			loadCollapsed?: boolean;
			allowEmptyStringAsNull?: boolean;
		} = {};
		for (const property of Object.keys(thisElement)) {
			if (property === "allowCreation") {
				booleanData[property] = false;
			}
			if (property === "isArray") {
				booleanData[property] = false;
			}
			if (property === "loadCollapsed") {
				booleanData[property] = false;
			}
			if (property === "allowEmptyStringAsNull") {
				booleanData[property] = false;
			}
			if (property === "searchOperator") {
				booleanData[property] = "contains";
			}
			if (property === "numberType") {
				booleanData[property] = "int";
			}
			if (
				property !== "elem" &&
				thisElement[property as propertyType]?.required
			) {
				jsonRequired.push(property as propertyType);
			}
		}

		const elementToAdd: ElementType = {
			elementId: uuidv4(),
			elem: data,
			jsonRequired: jsonRequired,
			formValidation: false,
			elementValidation: false,
			elementLayout: {
				proportionalWidth: null,
				lineBreakAfterElement: false,
			},
			...booleanData,
		};
		dispatch({ type: ADD_ELEMENT, data: elementToAdd });
	}, []);

	const moveElement = useCallback((data: MoveData) => {
		dispatch({ type: MOVE_ELEMENT, data });
	}, []);

	const removeElement = useCallback((data: number) => {
		dispatch({ type: REMOVE_ELEMENT, data });
	}, []);

	const updateElement = useCallback((data: UpdateData) => {
		dispatch({ type: UPDATE_ELEMENT, data });
	}, []);

	const initForm = useCallback((data: FormSetup) => {
		dispatch({ type: INIT_FORM, data });
	}, []);

	const updateFormLabel = useCallback((data: string) => {
		dispatch({ type: UPDATE_FORM_LABEL, data });
	}, []);

	const updateEntityData = useCallback((data: IdData) => {
		dispatch({ type: UPDATE_ENTITY_DATA, data });
	}, []);

	const updateValidation = useCallback((data: boolean) => {
		dispatch({ type: UPDATE_VALIDATION, data });
	}, []);

	const updateElementValidation = useCallback((data: ElementValidation) => {
		dispatch({ type: UPDATE_ELEMENT_VALIDATION, data });
	}, []);

	const initNestedElements = useCallback((data: NestedType) => {
		dispatch({ type: INIT_NESTED_ELEMENTS, data });
	}, []);

	const loadForm = useCallback((data: State) => {
		dispatch({ type: LOAD_FORM, data });
	}, []);

	const updateFormBusinessLogic = useCallback(
		(data: BusinessLogicUpdateData<formBusinessLogicPropertyType>) => {
			dispatch({ type: UPDATE_FORM_BUSINESS_LOGIC, data });
		},
		[]
	);

	const updateFormBusinessLogicValidation = useCallback(
		(data: FormBusinessLogicUpdateDataValidation) => {
			dispatch({ type: UPDATE_FORM_BUSINESS_LOGIC_VALIDATION, data });
		},
		[]
	);

	const updateFormLayoutFullWidth = useCallback(
		(data: FormLayout["fullWidth"]) => {
			dispatch({ type: UPDATE_FORM_LAYOUT_FULL_WIDTH, data });
		},
		[]
	);

	const updateFormLayoutMaxWidth = useCallback(
		(data: FormLayout["maxWidth"]) => {
			dispatch({ type: UPDATE_FORM_LAYOUT_MAX_WIDTH, data });
		},
		[]
	);

	const updateSelectFieldsBasedOnForm = useCallback(
		(data: SelectFieldsType) => {
			dispatch({ type: UPDATE_SELECT_FIELDS_BASED_ON_FORM, data });
		},
		[]
	);

	const updateAdditionalSelect = useCallback(
		(data: SearchSelectOptionProps[]) => {
			dispatch({ type: UPDATE_ADDITIONAL_SELECT, data });
		},
		[]
	);

	return {
		state,
		addElement,
		moveElement,
		removeElement,
		updateElement,
		initForm,
		updateFormLabel,
		updateEntityData,
		updateValidation,
		updateElementValidation,
		initNestedElements,
		loadForm,
		updateFormBusinessLogic,
		updateFormBusinessLogicValidation,
		updateFormLayoutFullWidth,
		updateFormLayoutMaxWidth,
		updateSelectFieldsBasedOnForm,
		updateAdditionalSelect,
	};
};
